import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PuzzleWorkspace, { PuzzlePanel } from '../components/puzzles/PuzzleWorkspace';
import { Button } from '../components/ui/button';
import { useWoodpeckerAuth } from './Woodpecker';
import PerformanceGraphs from './PerformanceGraphs';
import PricingModal from './PricingModal';
import { FaChevronDown, FaChevronUp, FaCrown } from 'react-icons/fa';
import WoodpeckerMethodContent from './WoodpeckerMethodContent';
import { Helmet } from "react-helmet";

const BriefingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleStartSession, isPremium } = useWoodpeckerAuth();
  const [summaryData, setSummaryData] = useState(null);
  const [sessionMode, setSessionMode] = useState('full');
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isMethodExpanded, setIsMethodExpanded] = useState(
    location.state?.cycleNumber === 1 && location.state?.currentTime === 0
  );

  useEffect(() => {
    if (location.state) {
      // If currentProgress exists, merge its values into the summaryData for easier access
      let data = { ...location.state };
      if (data.currentProgress) {
        data = {
          ...data,
          midCycle: Boolean(data.currentProgress.sessionMode || data.currentProgress.puzzlesCompleted > 0 || data.currentProgress.currentTime > 0),
          currentTime: data.currentProgress.currentTime,
          lastUpdated: data.currentProgress.lastUpdated,
          puzzleIndex: data.currentProgress.puzzlesCompleted, // Use puzzlesCompleted as puzzleIndex
          solvedCorrectly: data.currentProgress.solvedCorrectly,
        };
      }
      console.log('BriefingPage data:', data);
      setSummaryData(data);
      setSessionMode(data.currentProgress?.sessionMode ||
        (data.cycles?.[data.cycles.length - 1]?.sessionMode === 'mistakes' && data.wrongPuzzleIds?.length ? 'mistakes' : 'full'));
    } else {
      console.warn('No state provided to BriefingPage.');
      navigate('/woodpecker');
    }
  }, [location.state, navigate]);

  if (!summaryData) {
    return null; // or a loading spinner
  }

  const {
    setId,
    setName,
    currentTime, 
    puzzleIndex, 
    cycleNumber, 
    solvedCorrectly,
    userEmail,
    userRating, 
    puzzleQuantity, 
    midCycle,
    wrongPuzzleIds = [],
    cycles = [] // Add cycles with default empty array
  } = summaryData;

  // Add validation check
  if (!userEmail || !userRating || !puzzleQuantity) {
    console.error('Missing required data:', { userEmail, userRating, puzzleQuantity });
    navigate('/woodpecker');
    return null;
  }

  console.log(currentTime, cycleNumber);
  // Determine if this is a briefing after cycle completion
  const includeCycleSummary = midCycle;

  // Calculate total puzzles
  const totalPuzzles = sessionMode === 'mistakes'
    ? (midCycle ? summaryData.currentProgress?.puzzleIds?.length : wrongPuzzleIds.length)
    : puzzleQuantity;

  // Calculate current cycle stats
  const currentCycleAccuracy = puzzleIndex > 0 
    ? Math.round((solvedCorrectly / puzzleIndex) * 100) // Not puzzleIndex + 1 because that would be including the current puzzle which hasn't been attempted yet
    : 0;

  // Format time from seconds to minutes and seconds
  const formatTime = (timeInSeconds) => {
    if (timeInSeconds === undefined || timeInSeconds === null) return 'N/A';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}m ${seconds}s`;
  };

  // Calculate cycle history stats
  const cycleHistory = cycles.map(cycle => ({
    ...cycle,
    accuracy: cycle.puzzlesCompleted > 0 
      ? Math.round((cycle.solvedCorrectly / cycle.puzzlesCompleted) * 100)
      : 0
  }));

  const handleStart = () => {
    handleStartSession({
      setId,
      setName,
      userEmail,
      userRating,
      puzzleQuantity,
      sessionMode,
      startPuzzleIndex: puzzleIndex || 0,
      initialTimeSpent: currentTime || 0,
      initialCycleNumber: cycleNumber,
      initialSolvedCorrectly: solvedCorrectly || 0
    });
  };

  const handleStartNextCycle = () => {
    if (userEmail && userRating && puzzleQuantity) {
      handleStartSession({
        setId,
        setName,
        userEmail,
        userRating,
        puzzleQuantity,
        sessionMode,
        startPuzzleIndex: 0,
        initialTimeSpent: 0,
        initialCycleNumber: cycleNumber,
        initialSolvedCorrectly: 0
      });
    } else {
        console.error("Missing information to start next cycle.", { userEmail, userRating, puzzleQuantity });
    }
  };

  const handleUpgradeClick = () => {
    setShowPricingModal(true);
  };

  // Check if user has reached cycle limit
  const hasReachedCycleLimit = !isPremium && cycleNumber > 1000000; // No limit for now

  return (
    <>
      <Helmet>
        <title>Woodpecker Training Progress | Puzzlify</title>
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href="https://puzzlify.io/woodpecker/briefing" />
      </Helmet>
      <PuzzleWorkspace title="Progress overview" subtitle={setName}
        backTo="/woodpecker" backLabel="Back to training sets" contentLabel="Training progress"
        toolbar={
          <Button variant="neutral" size="action" className="m-0 w-12 border-solid p-0"
            onClick={handleUpgradeClick} aria-label={isPremium ? 'Premium membership' : 'Upgrade to Premium'}
            title={isPremium ? 'Premium membership' : 'Upgrade to Premium'}>
            <FaCrown aria-hidden="true" className="text-amber-600" />
          </Button>
        }
      >
        <div className="space-y-6 px-4 sm:px-0">
          <PuzzlePanel title="Your training plan">
            <dl className="grid grid-cols-2 gap-4">
              <div><dt className="text-sm text-slate-600">Full training set</dt><dd className="mt-1 text-xl font-semibold">{puzzleQuantity} puzzles</dd></div>
              <div><dt className="text-sm text-slate-600">Puzzle difficulty</dt><dd className="mt-1 text-xl font-semibold">{userRating}</dd></div>
            </dl>
          </PuzzlePanel>

          {includeCycleSummary && (
            <PuzzlePanel title="Current progress">
              <dl className="grid grid-cols-2 gap-5 sm:grid-cols-4">
                <div><dt className="text-sm text-slate-600">Completed</dt><dd className="mt-1 font-semibold">{puzzleIndex} / {totalPuzzles}</dd></div>
                <div><dt className="text-sm text-slate-600">Time spent</dt><dd className="mt-1 font-semibold">{formatTime(currentTime)}</dd></div>
                <div><dt className="text-sm text-slate-600">Accuracy</dt><dd className="mt-1 font-semibold">{currentCycleAccuracy}%</dd></div>
                <div><dt className="text-sm text-slate-600">Correctly solved</dt><dd className="mt-1 font-semibold">{solvedCorrectly} / {puzzleIndex}</dd></div>
              </dl>
            </PuzzlePanel>
          )}

          <PuzzlePanel title={includeCycleSummary ? `Continue cycle ${cycleNumber}` : `Starting cycle ${cycleNumber}`}>
            <p className="text-sm leading-relaxed text-slate-600">
              {midCycle
                ? `You have ${totalPuzzles - puzzleIndex} puzzles remaining in this cycle. Continue where you left off!`
                : cycleNumber > 1
                  ? `You've completed Cycle ${cycleNumber - 1}! Choose the full set or repeat your remaining mistakes for Cycle ${cycleNumber}.`
                  : `You are about to start your first cycle of the Woodpecker Method with ${totalPuzzles} puzzles. Focus on solving them accurately at your own pace.`}
            </p>
            <fieldset className="my-5 min-w-0" disabled={midCycle}>
              <legend className="mb-3 text-sm font-semibold text-slate-800">{midCycle ? 'Current cycle' : 'What would you like to repeat?'}</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className={`flex min-h-16 items-center gap-3 rounded-xl border p-4 ${sessionMode === 'full' ? 'border-sky-700 bg-sky-50' : 'border-slate-200 bg-white'} ${midCycle ? '' : 'cursor-pointer'}`}>
                  <input type="radio" name="sessionMode" value="full" checked={sessionMode === 'full'} onChange={() => setSessionMode('full')} className="h-4 w-4 shrink-0 accent-sky-700" />
                  <span><strong>Full set</strong><span className="block text-sm text-slate-600">{puzzleQuantity} puzzles</span></span>
                </label>
                <label className={`flex min-h-16 items-center gap-3 rounded-xl border p-4 ${sessionMode === 'mistakes' ? 'border-sky-700 bg-sky-50' : 'border-slate-200 bg-white'} ${!wrongPuzzleIds.length && sessionMode !== 'mistakes' ? 'opacity-60' : 'cursor-pointer'}`}>
                  <input type="radio" name="sessionMode" value="mistakes" checked={sessionMode === 'mistakes'} disabled={!wrongPuzzleIds.length && sessionMode !== 'mistakes'} onChange={() => setSessionMode('mistakes')} className="h-4 w-4 shrink-0 accent-sky-700" />
                  <span><strong>Mistakes only</strong><span className="block text-sm text-slate-600">{midCycle && sessionMode === 'mistakes' ? `${totalPuzzles} puzzles this cycle` : `${wrongPuzzleIds.length} puzzles remaining`}</span></span>
                </label>
              </div>
              <p className="mb-0 mt-3 text-sm leading-relaxed text-slate-600">
                {midCycle
                  ? 'Finish this cycle before changing the selection.'
                  : wrongPuzzleIds.length
                    ? 'Only wrong moves add puzzles here. Solve them without a wrong move to remove them, then repeat the smaller set next cycle. Each round counts as a normal cycle.'
                    : summaryData.wrongPuzzleIds === undefined
                      ? 'Mistakes from older cycles were not recorded. Play a full-set cycle to start tracking wrong puzzles.'
                      : 'No wrong puzzles to repeat. Play the full set to keep training.'}
              </p>
            </fieldset>
            <Button variant="brand" size="action" className="m-0 w-full sm:w-auto"
              onClick={midCycle ? handleStart : handleStartNextCycle} disabled={hasReachedCycleLimit}>
              {midCycle ? `Continue Cycle ${cycleNumber}` : `Start Cycle ${cycleNumber}`}{sessionMode === 'mistakes' ? ' · Mistakes only' : ''}
            </Button>
            {hasReachedCycleLimit && (
              <div className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-slate-700">
                <p>Upgrade to Premium for unlimited cycles.</p>
                <Button variant="neutral" size="action" onClick={handleUpgradeClick}>Upgrade now</Button>
              </div>
            )}
          </PuzzlePanel>

          {cycleHistory.length > 0 && (
            <>
              <PuzzlePanel title="Cycle history">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {cycleHistory.map(cycle => (
                    <section key={cycle.cycleNumber} className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <h3 className="mb-3 text-base font-semibold">Cycle {cycle.cycleNumber}{cycle.sessionMode === 'mistakes' ? ' · Mistakes only' : ''}</h3>
                      <dl className="space-y-2 text-sm">
                        <div className="flex flex-wrap justify-between gap-2"><dt className="text-slate-600">Time</dt><dd>{formatTime(cycle.timeSpent)}</dd></div>
                        <div className="flex flex-wrap justify-between gap-2"><dt className="text-slate-600">Accuracy</dt><dd>{cycle.accuracy}%</dd></div>
                        <div className="flex flex-wrap justify-between gap-2"><dt className="text-slate-600">Correctly solved</dt><dd>{cycle.solvedCorrectly} / {cycle.puzzlesCompleted}</dd></div>
                        <div><dt className="text-slate-600">Completed</dt><dd className="break-words">{new Date(cycle.completedAt).toLocaleString()}</dd></div>
                      </dl>
                    </section>
                  ))}
                </div>
              </PuzzlePanel>
              <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <PerformanceGraphs cycles={cycleHistory} currentCycle={{
                  cycleNumber,
                  accuracy: puzzleIndex > 0 ? (solvedCorrectly / puzzleIndex) * 100 : 0,
                  timeSpent: currentTime
                }} />
              </div>
            </>
          )}

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="m-0 text-base">
              <Button variant="neutral" size="action" className="m-0 w-full justify-between gap-4 border-solid text-left"
                aria-expanded={isMethodExpanded} aria-controls="woodpecker-method-details"
                onClick={() => setIsMethodExpanded(!isMethodExpanded)}>
                What is the Woodpecker Method?
                {isMethodExpanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
              </Button>
            </h2>
            {isMethodExpanded && <div id="woodpecker-method-details" className="pt-4"><WoodpeckerMethodContent /></div>}
          </section>
        </div>
      </PuzzleWorkspace>
      <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </>
  );
};
export default BriefingPage;
